import styled from "@emotion/styled";
import { useMemo, useState, type MouseEvent, type ReactNode } from "react";

import { Button, Icon, Menu, MenuItem, Tooltip } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import type { ImportTarget } from "@skilitics/nautilus-core-designer-data";

import type { ImportNodeTarget, NodeSelectionCriteria } from "../library/index.ts";
import { useBrowseTemplates } from "../nodes/templates/index.ts";
import { useManagedSlotPasteClipboard } from "../slots/index.ts";

type Props = {
  disabled?: boolean;
  tooltip?: ReactNode;
  browseLabel: string;
  pasteLabel: string;
  browseTarget: ImportNodeTarget;
  templateCriteria: NodeSelectionCriteria;
  onCreateYourOwn: () => void;
  ["data-test"]: string;
};

// The thumbnail nav bar "+" button for adding an overlay or frame. Opens a menu offering
// "Create your own" (the plain add flow), browsing the template library, or pasting a copied item.
export const AddThumbnailItemButton = ({
  disabled,
  tooltip,
  browseLabel,
  pasteLabel,
  browseTarget,
  templateCriteria,
  onCreateYourOwn,
  ...rest
}: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const browseTemplates = useBrowseTemplates({ target: browseTarget, criteria: templateCriteria });

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleButtonClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleCreateYourOwn = () => {
    handleClose();
    onCreateYourOwn();
  };

  const handleBrowse = () => {
    handleClose();
    browseTemplates();
  };

  const pasteTarget = useMemo<ImportTarget | undefined>(() => {
    if ("slotId" in browseTarget) return { slotId: browseTarget.slotId };
    if ("slotResidentNodeId" in browseTarget) return { slotResidentNodeId: browseTarget.slotResidentNodeId };
    return undefined;
  }, [browseTarget]);

  const title = !disabled && !anchorEl ? tooltip : "";

  return (
    <>
      <Tooltip title={title} slotProps={{ tooltip: { sx: { top: "4px" } } }}>
        <Container>
          <StyledButton disabled={disabled} onClick={handleButtonClick} data-test={rest["data-test"]}>
            <IconWrapper>
              <Icon icon="plus" />
            </IconWrapper>
          </StyledButton>
        </Container>
      </Tooltip>

      <Menu
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        data-test="add-thumbnail-item-menu"
      >
        <MenuItem onClick={handleCreateYourOwn} data-test="create-your-own">
          {formatMessage("createYourOwn")}
        </MenuItem>
        <MenuItem onClick={handleBrowse} data-test="browse-templates">
          {browseLabel}
        </MenuItem>
        {pasteTarget && (
          <PasteMenuItem target={pasteTarget} criteria={templateCriteria} label={pasteLabel} onPasted={handleClose} />
        )}
      </Menu>
    </>
  );
};

type PasteMenuItemProps = {
  target: ImportTarget;
  criteria: NodeSelectionCriteria;
  label: string;
  onPasted: () => void;
};

const PasteMenuItem = ({ target, criteria, label, onPasted }: PasteMenuItemProps) => {
  const { canPasteHere, paste } = useManagedSlotPasteClipboard({ target, criteria });

  const handleClick = () => {
    paste();
    onPasted();
  };

  return (
    <Tooltip title={!canPasteHere.value ? canPasteHere.message : ""}>
      <span>
        <MenuItem onClick={handleClick} disabled={!canPasteHere.value} data-test="paste-thumbnail-item">
          {label}
        </MenuItem>
      </span>
    </Tooltip>
  );
};

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  height: 30px;
  width: 30px;

  background: ${({ theme }) => theme.neutralContainer};

  border-radius: 50%;
  font-size: 18px;

  transition: background-color 200ms ease-in;
`;

const Container = styled.div`
  height: 100%;
  width: 100%;
`;

const StyledButton = styled(Button)<{ disabled?: boolean }>`
  height: 100%;
  width: 100%;

  color: ${({ theme }) => theme.onNeutral};

  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};

  &:hover {
    ${IconWrapper} {
      background: ${({ theme }) => theme.primary};
    }
  }
`;

const formatStrings = {
  createYourOwn: {
    en: "Create Your Own",
  },
} satisfies FormatMessages;
